import {
  CalendarFilled,
  DeleteFilled,
  DollarCircleFilled,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Table } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteLedgerExpense } from "../../api/api.delete";
import {
  useGetLedgerById,
  useGetLedgerTransactions,
  useGetLedgerTransactionsTotal,
} from "../../api/api.get";
import ConfirmDialog from "../../components/dialog/confirm.dialog";
import CreateLedgerExpenseDialog from "../../components/dialog/create.ledger.expense.dialog";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useAuthContext } from "../../providers/auth.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { capitalize, getProductCurrencySymbol } from "../../utils/functions";
import { toFixedDisplay } from "../../utils/number.utils";
import { IExpense2 } from "../../types/types";

export default function LedgerViewPage() {
  const { id: ledgerId } = useParams();
  const { defaultGroupId } = useGroupContext();
  const navigate = useNavigate();

  const { user } = useAuthContext();
  const { notification, notifyError, notifySuccess } = useNotifications();

  const { mutateAsync: deleteAsync } = useDeleteLedgerExpense(
    defaultGroupId,
    ledgerId,
  );
  const {
    data: ledgerData,
    isPending: isTripDataLoading,
    refetch: refetchLedger,
  } = useGetLedgerById(defaultGroupId, ledgerId);

  const { data: transactionsTotal, refetch: refetchTransactionsTotal } =
    useGetLedgerTransactionsTotal(defaultGroupId, ledgerId);

  const {
    data: transactions,
    isPending: isTransactionsLoading,
    refetch: refetchTransactions,
  } = useGetLedgerTransactions(defaultGroupId, ledgerId);

  const expenses = useMemo(() => {
    if (!transactions) return [];

    if (transactions) {
      const results: any = transactions.map((expense) => ({
        key: expense.id,
        name: expense.comments,
        category: expense.category,
        amount: expense.amount,
        expensedate: expense.expenseDate,
        method: expense.cardname,
        cardname: expense.cardname,
        comments: expense.comments,
        created_at: expense.created_at,
        creditcard: expense.creditcard,
        expense_source: expense.expense_source,
        expense_date: expense.expenseDate,
        groupid: "",
        id: expense.id,
        subcategory: expense.subcategory,
        tripid: expense.tripid,
        username: expense.username,
      }));

      return results;
    }
  }, [transactions]);

  const [deleteableExpenseId, setDeleteableExpenseId] = useState<
    string | undefined
  >();

  const columns = [
    {
      title: "Description",
      dataIndex: "name",
      key: "name",
    },

    {
      title: "Amount",
      key: "amount",
      render: (item: IExpense2) => toFixedDisplay(item.amount),
      sorter: (a: IExpense2, b: IExpense2) => a.amount - b.amount,
      sortDirections: ["descend", "ascend"] as any[],
    },
    {
      title: "Date",
      dataIndex: "expensedate",
      key: "expensedate",
      render: (date: string) => (
        <span>{dayjs(date).format("DD MMM YYYY")}</span>
      ),
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
    },

    {
      title: "",
      width: 30,
      render: (_: any, record: IExpense2) => (
        <Button
          type="text"
          icon={<DeleteFilled />}
          onClick={() => setDeleteableExpenseId(record.id)}
          disabled={record.username !== user?.username}
        />
      ),
    },
  ];

  const handleDeleteExpense = () => {
    deleteAsync({ expenseId: deleteableExpenseId! })
      .then(() => {
        refetchTransactions();
        refetchLedger();
        refetchTransactionsTotal();
      })
      .catch(() => notifyError("Whoops! Please try again."))
      .finally(() => setDeleteableExpenseId(undefined));
  };
  const [createMode, setCreateMode] = useState<boolean>(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="pt-4 pb-12 px-4 sm:px-6 lg:px-8">
        {notification}
        {deleteableExpenseId && (
          <ConfirmDialog
            open={!!deleteableExpenseId}
            onCancel={() => setDeleteableExpenseId(undefined)}
            onOk={handleDeleteExpense}
            title="Are you sure you would like to delete this expense?"
          />
        )}
        {createMode && (
          <CreateLedgerExpenseDialog
            ledgerId={ledgerData!.id}
            groupId={defaultGroupId}
            open={createMode}
            onSuccess={() => {
              notifySuccess({
                title: "Transaction",
                description: "Created successfully.",
              });
              setCreateMode(false);
              refetchTransactions();
              refetchTransactionsTotal();
            }}
            onCancel={() => setCreateMode(false)}
          />
        )}
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Button type="link" onClick={() => navigate("/ledgers")}>
            Back
          </Button>

          {/* Two Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Trip Details */}
            <div className="lg:col-span-1 space-y-6">
              {/* Trip Header Card */}
              <Card
                loading={isTripDataLoading}
                className="bg-gradient-to-br from-primary/10 to-indigo-100 border-primary/20"
              >
                <CardContent className="pt-6">
                  <div className="mb-4">
                    <h1 className="text-3xl font-bold text-foreground mb-2">
                      {capitalize(ledgerData?.name)}
                    </h1>
                    <div className="flex items-center gap-2 text-primary font-semibold">
                      {ledgerData?.currency}
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-primary/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <UserOutlined className="w-4 h-4" />
                        <span className="text-sm">Shared</span>
                      </div>
                      <span className="text-md font-semibold text-foreground">
                        {ledgerData?.shared ? "Yes" : "No"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <DollarCircleFilled className="w-4 h-4" />
                        <span className="text-sm">Grand Total</span>
                      </div>
                      <span className="text-md font-bold text-primary">
                        {getProductCurrencySymbol(ledgerData?.currency)}
                        {toFixedDisplay(ledgerData?.total)}
                      </span>
                    </div>
                    <p className="text-sm">
                      Target total to achieve from this ledger.
                    </p>

                    {!!ledgerData && ledgerData?.total > 0 && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <DollarCircleFilled className="w-4 h-4" />
                          <span className="text-sm">Paid </span>
                        </div>
                        <span className="text-md font-bold text-primary text-green-400">
                          {getProductCurrencySymbol(ledgerData?.currency)}
                          {toFixedDisplay(transactionsTotal?.total ?? 0)}
                        </span>
                      </div>
                    )}

                    {!!ledgerData && ledgerData?.total > 0 && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <DollarCircleFilled className="w-4 h-4" />
                          <span className="text-sm">Balance </span>
                        </div>
                        <span className="text-md font-bold text-primary text-blue-500">
                          {getProductCurrencySymbol(ledgerData?.currency)}
                          {toFixedDisplay(
                            (ledgerData?.total ?? 0) -
                              (transactionsTotal?.total ?? 0),
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarFilled className="w-5 h-5 text-primary" />
                    Transactions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-row justify-between items-center">
                    <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">
                      Total Transactions:
                    </p>
                    <p className="font-semibold text-foreground">
                      {transactions?.length}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Panel: Expenses List */}
            <div className="lg:col-span-2">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarCircleFilled className="w-5 h-5 text-primary" />
                    Transactions
                  </CardTitle>
                  <CardDescription>
                    <div className="flex justify-between">
                      <div>
                        {transactions?.length ?? 0} expenses recorded for this
                        ledger
                      </div>

                      <Button type="link" onClick={() => setCreateMode(true)}>
                        Add Transactions
                      </Button>
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div>
                    <div className="overflow-x-auto">
                      <Table
                        dataSource={expenses}
                        columns={columns}
                        loading={isTransactionsLoading}
                        pagination={{ pageSize: 50 }}
                      />
                    </div>
                  </div>

                  {/* Summary Footer */}
                  <div className="mt-6 pt-4 border-t-2 border-slate-200 flex justify-between items-center">
                    <p className="font-semibold text-foreground">Total</p>
                    <p className="text-2xl font-bold text-primary">
                      {getProductCurrencySymbol(ledgerData?.currency)}
                      {toFixedDisplay(transactionsTotal?.total)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
